'use client'

import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import CreationToolStep from 'creation/src/creation-tools/types/steps'
import { usePathname } from 'next/navigation'
import { toolBackgroundVideoUrl } from './presentation/assets'
import { useCreationStore } from './store/provider'

type Props = {
    basePath: string
}

// Legacy: components/background.tsx. `baseStyles` there is
// `display:block; position:fixed; inset 0/0; width/height 100%; opacity:0.3;
// pointer-events:none; z-index:-1`, with the video adding `object-fit:cover`.
const BASE_CLASS = 'pointer-events-none fixed top-0 left-0 -z-10 block h-full w-full opacity-30'

/**
 * Legacy shows the video only on the welcome screen — it compares the router's
 * pathname against the tool's base path (`pathname === basePath`), so the
 * moment a source is chosen and the flow advances to a step route, the video
 * goes away. Reproduced here with the same equality check rather than a
 * "starts with" test, which would keep it on every step.
 *
 * The sticker checkerboard is the other half of legacy's branch and applies on
 * any step once the output type is Sticker.
 *
 * DIVERGENCE, spec §12.16: the video also plays on the CutOut step, where
 * legacy shows only the checkerboard. That step spends most of its life as a
 * waiting screen — an animated source runs background removal on mount, which
 * is one request per third of a second of footage — and against a flat
 * checkerboard it reads as a stalled page. The canvas keeps its own
 * checkerboard (`stickerBlock`), so nothing is lost: this is the page behind
 * the card, exactly as on the welcome screen.
 */
const Background = ({ basePath }: Props) => {
    const tool = useCreationStore((state) => state.tool)
    const outputType = useCreationStore((state) => state.outputType)
    const pathname = usePathname()
    const backgroundVideoUrl = toolBackgroundVideoUrl(tool)

    // Trailing slashes: legacy's react-router pathname never carries one, but a
    // Next route can be hit either way, so normalize before comparing.
    const normalized = pathname.replace(/\/$/, '')
    const isWelcome = normalized === basePath.replace(/\/$/, '')
    const isCutOut = normalized === `${basePath}${CreationToolStep.route(CreationToolStep.CutOut)}`.replace(/\/$/, '')

    if (backgroundVideoUrl && (isWelcome || isCutOut)) {
        return (
            <video className={`${BASE_CLASS} object-cover`} src={backgroundVideoUrl} muted playsInline autoPlay loop />
        )
    }

    if (outputType === CreationToolOutputType.Sticker) {
        // Legacy's StickerBlock (components/blocks.ts): the same two 45deg
        // gradients, 24px tile, second layer offset by half a tile, on black.
        return (
            <div
                className={`${BASE_CLASS} bg-black [background-image:linear-gradient(45deg,#0e0e0e_25%,transparent_25%,transparent_75%,#0e0e0e_75%),linear-gradient(45deg,#0e0e0e_25%,transparent_25%,transparent_75%,#0e0e0e_75%)] [background-position:0_0,12px_12px] [background-size:24px_24px]`}
            />
        )
    }

    return null
}

export default Background
